import Box from '@mui/material/Box';
import { Drawer } from '@mui/material';
import moment from 'moment';
import { arrayOf, object } from 'prop-types';
import React, { useEffect } from 'react';
import Mandatory from 'services/api/Mandatory';
import channelShape from 'shapes/channel';
import employeeShape from 'shapes/employee';
import CloseButton from '../CloseButton';
import Analytics from './Analytics';

import connector from './connector';
import Headline from './Headline';

const AnalyticsDrawer = ({
  actions,
  channel,
  employee,
  analyticsDrawer: { isOpen, mandatory },
}) => {
  useEffect(() => {
    if (isOpen && channel?._id && !channel?.isDirect) {
      actions.channels.find(channel._id);
    }
  }, [channel?._id, actions, isOpen, channel?.isDirect]);

  const handleClose = () => {
    actions.drawer.analyticsDrawer.close();
  };

  const openModal = message => () => {
    actions.acknowledgmentReadModal.open(message, channel, employee);
  };

  const handleExport = async () => {
    if (!mandatory) {
      return;
    }
    // Same source as the monitor list (GET /v1/mandatory/:id), not `channel.employees` — DM channels
    // often omit or under-populate `channel.employees`, which produced empty XLSX rows (OMM-592).
    const { acknowledges = [], employees = [] } = await Mandatory.find(mandatory._id, {
      perPage: 100000,
      page: 1,
    });
    const XLSX = await import('xlsx');
    const rows = employees.map(e => {
      const uid = e.user_id ?? e.user?._id;
      const ack = acknowledges.find(a => String(a.user) === String(uid));
      const confirmedAt =
        ack?.confirmedAt != null
          ? new Date(ack.confirmedAt * 1000)
          : e.confirmedAt != null
            ? new Date(e.confirmedAt * 1000)
            : '';
      return [e.firstName ?? '', e.surName ?? '', e.code ?? '', confirmedAt];
    });
    const data = [['givenName', 'familyName', 'code', 'confirmedAt'], ...rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');
    const now = moment();
    XLSX.writeFile(wb, `${now.format('YYYY_MM_DD')}_${mandatory.text}.xlsx`, {
      bookType: 'xlsx',
      type: 'array',
    });
  };

  if (!mandatory) return null;

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <Box
        sx={theme => ({
          display: 'flex',
          flexGrow: 1,
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: 1000,
          },
        })}
      >
        <Box sx={{ pt: '20px', pl: '20px' }}>
          <CloseButton onClick={handleClose} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            p: '20px',
          }}
        >
          <Headline onOpen={openModal(mandatory)} onExport={handleExport} name={mandatory.text} />
          <Analytics mandatoryId={mandatory._id} message={mandatory} />
        </Box>
      </Box>
    </Drawer>
  );
};

AnalyticsDrawer.propTypes = {
  channel: channelShape.isRequired,
  employee: employeeShape.isRequired,
  actions: object.isRequired,
  analyticsDrawer: object.isRequired,
  members: arrayOf(employeeShape).isRequired,
};

export default connector(AnalyticsDrawer);
